import { z } from 'zod';

/**
 * Zod schemas mirroring `packages/models/src/appointments/BookingSettings.ts`.
 *
 * Per-restaurant booking-policy singleton. Money is integer cents; percentages
 * are integer basis points (bps): 1% = 100 bps, so a 25% deposit is
 * `percentBps: 2500`.
 */

/** How a booking becomes confirmed (`CONFIRMATION_MODES`). */
export const ConfirmationModeSchema = z.enum(['auto', 'manual']);

/** Fee-charging strategy for deposits / no-show fees (`POLICY_MODES`). */
export const PolicyModeSchema = z.enum(['none', 'flat', 'percent']);

/** Deposit taken at booking time. `amountCents`/`percentBps` used per `mode`. */
const DepositPolicySchema = z
  .object({
    mode: PolicyModeSchema,
    /** Flat charge in integer cents (used when `mode === 'flat'`). */
    amountCents: z.number().int().nonnegative().optional(),
    /** Percentage in integer basis points (used when `mode === 'percent'`). */
    percentBps: z.number().int().nonnegative().optional(),
  })
  .strict();

/** No-show fee policy, carrying the exact consent copy the guest agreed to. */
const NoShowFeePolicySchema = z
  .object({
    mode: PolicyModeSchema,
    /** Flat charge in integer cents (used when `mode === 'flat'`). */
    amountCents: z.number().int().nonnegative().optional(),
    /** Percentage in integer basis points (used when `mode === 'percent'`). */
    percentBps: z.number().int().nonnegative().optional(),
    /** The exact consent copy the guest agreed to authorizing this fee. */
    consentText: z.string(),
  })
  .strict();

export const BookingSettingsSchema = z
  .object({
    /** Minimum minutes between "now" and the start of a bookable slot. */
    minLeadTimeMinutes: z.number().int().nonnegative(),
    /** How far into the future (in days) a guest may book. */
    maxAdvanceDays: z.number().int().nonnegative(),
    /** Slot grid size in minutes (e.g. 15 → :00/:15/:30/:45). */
    slotGranularityMinutes: z.number().int().positive(),
    /** Hours before start within which cancellation is no longer free. */
    cancellationWindowHours: z.number().int().nonnegative(),
    confirmationMode: ConfirmationModeSchema,
    allowGuestBooking: z.boolean(),
    allowAnyProvider: z.boolean(),
    depositPolicy: DepositPolicySchema,
    noShowFeePolicy: NoShowFeePolicySchema,
    /** Minutes-before-start at which reminders fire, e.g. `[1440, 120]`. */
    reminderOffsetsMinutes: z.array(z.number().int().nonnegative()),
    createdAt: z.date(),
    updatedAt: z.date(),
  })
  .strict();

/** Request body for updating booking settings (staff). Partial patch; server owns timestamps. */
export const UpdateBookingSettingsSchema = z
  .object({
    minLeadTimeMinutes: z.number().int().nonnegative().optional(),
    maxAdvanceDays: z.number().int().nonnegative().optional(),
    slotGranularityMinutes: z.number().int().positive().optional(),
    cancellationWindowHours: z.number().int().nonnegative().optional(),
    confirmationMode: ConfirmationModeSchema.optional(),
    allowGuestBooking: z.boolean().optional(),
    allowAnyProvider: z.boolean().optional(),
    depositPolicy: DepositPolicySchema.optional(),
    noShowFeePolicy: NoShowFeePolicySchema.optional(),
    reminderOffsetsMinutes: z.array(z.number().int().nonnegative()).optional(),
  })
  .strict();

export type ConfirmationModeType = z.infer<typeof ConfirmationModeSchema>;
export type PolicyModeType = z.infer<typeof PolicyModeSchema>;
export type DepositPolicyType = z.infer<typeof DepositPolicySchema>;
export type NoShowFeePolicyType = z.infer<typeof NoShowFeePolicySchema>;
export type BookingSettingsType = z.infer<typeof BookingSettingsSchema>;
export type UpdateBookingSettingsType = z.infer<typeof UpdateBookingSettingsSchema>;
